import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { AlertTriangle, BookOpen, ChevronRight, Plus, RefreshCw, Search, X } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { CATEGORIES, STATUS_LABELS, STATUS_TONES, VIEW_TABS, canManageKnowledge, knowledgeError } from "../lib/knowledge";
import { formatDay } from "../lib/governance";
import { Chip } from "../components/governance/GovernanceBits";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Skeleton } from "../components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";

const EMPTY = {
  published: ["Aucun article publié", "Les procédures, la documentation et les réponses aux questions fréquentes de l'équipe se retrouvent ici."],
  to_review: ["Rien à relire", "Les articles dont la date de relecture est passée apparaissent ici."],
  drafts: ["Aucun brouillon", "Un brouillon est une version en cours de rédaction, invisible des lecteurs."],
  archived: ["Aucun article archivé", "Un article archivé n'est plus visible des lecteurs mais reste conservé."],
  all: ["Aucun article pour le moment", "Écrivez la première procédure ou la première réponse à une question fréquente."],
};

const Chips = ({ a, testid }) => (
  <span className="inline-flex flex-wrap items-center gap-1.5">
    {a.status !== "published" && <Chip label={STATUS_LABELS[a.status]} tone={STATUS_TONES[a.status]} testid={testid} />}
    {a.has_draft && a.status === "published" && <Chip label="Brouillon en cours" tone="amber" />}
    {a.to_review && <Chip label="À relire" tone="amber" testid={`${testid}-review`} />}
  </span>
);

export default function KnowledgeArticles() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const canManage = canManageKnowledge(user);
  const view = canManage && VIEW_TABS.some(([id]) => id === params.get("view")) ? params.get("view") : "published";
  const search = params.get("q") || "";
  const category = params.get("category") || "";
  const [searchInput, setSearchInput] = useState(search);
  const [data, setData] = useState({ items: [], total: 0, counts: {} });
  const [phase, setPhase] = useState("loading");
  const [error, setError] = useState("");
  const seq = useRef(0);

  const update = (changes) => setParams((prev) => {
    const next = new URLSearchParams(prev);
    for (const [k, v] of Object.entries(changes)) { if (v) next.set(k, v); else next.delete(k); }
    return next;
  }, { replace: true });
  useEffect(() => {
    const t = setTimeout(() => { if (searchInput !== search) update({ q: searchInput.trim() }); }, 300);
    return () => clearTimeout(t);
  }, [searchInput]); // eslint-disable-line react-hooks/exhaustive-deps

  const load = useCallback(async () => {
    const id = ++seq.current;
    setPhase((p) => (p === "ready" ? p : "loading"));
    try {
      const { data: body } = await api.get("/knowledge/articles", { params: { view, limit: 200, ...(search ? { q: search } : {}), ...(category ? { category } : {}) } });
      if (id !== seq.current) return;
      setData(body);
      setPhase("ready");
    } catch (e) {
      if (id !== seq.current) return;
      setError(knowledgeError(e, "Impossible de charger les articles."));
      setPhase("error");
    }
  }, [view, search, category]);
  useEffect(() => { load(); return () => { seq.current += 1; }; }, [load]);

  const filtered = !!(search || category);
  const reset = () => { setSearchInput(""); update({ q: "", category: "" }); };
  const open = (a) => navigate(`/support/connaissances/${a.id}`);

  return (
    <div className="space-y-4" data-testid="knowledge-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="font-display font-bold text-2xl text-slate-900">Base de connaissance</h1>
          <p className="text-sm text-slate-500">Procédures internes, documentation du support, questions fréquentes, modèles et politiques : écrits une fois, relus régulièrement.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {canManage && <Button asChild data-testid="article-new"><Link to="/support/connaissances/nouveau"><Plus size={16} className="mr-1.5" />Nouvel article</Link></Button>}
        </div>
      </div>
      {canManage && (
        <div className="flex gap-1 overflow-x-auto pb-1 sm:flex-wrap sm:overflow-visible sm:pb-0" role="tablist" aria-label="Articles">
          {VIEW_TABS.map(([id, label]) => (
            <button key={id} type="button" role="tab" aria-selected={view === id} onClick={() => update({ view: id === "published" ? "" : id })} data-testid={`articles-tab-${id}`}
              className={`rounded-full px-3 py-1.5 text-sm font-medium whitespace-nowrap shrink-0 ${view === id ? "bg-jamora text-white" : "bg-white text-slate-600 border border-slate-200 hover:bg-slate-50"}`}>
              {label}{data.counts?.[id] != null && <span className={`ml-1.5 tabular-nums ${view === id ? "text-white/80" : "text-slate-400"}`} data-testid={`articles-count-${id}`}>{data.counts[id]}</span>}
            </button>
          ))}
        </div>
      )}
      <div className="relative max-w-xl">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" aria-hidden="true" />
        <Input value={searchInput} onChange={(e) => setSearchInput(e.target.value)} placeholder="Chercher une procédure, une réponse, un mot-clé…" className="pl-9 h-10" aria-label="Chercher dans la base de connaissance" data-testid="articles-search" />
      </div>
      <div className="flex gap-1.5 overflow-x-auto pb-1 sm:flex-wrap sm:overflow-visible sm:pb-0" aria-label="Catégories">
        {CATEGORIES.map(([id, label]) => (
          <button key={id} type="button" aria-pressed={category === id} onClick={() => update({ category: category === id ? "" : id })} data-testid={`articles-category-${id}`}
            className={`rounded-full px-3 py-1 text-sm whitespace-nowrap shrink-0 ${category === id ? "bg-slate-900 text-white" : "bg-white text-slate-600 border border-slate-200 hover:bg-slate-50"}`}>{label}</button>
        ))}
        {filtered && <Button variant="ghost" size="sm" className="h-8 text-slate-600 shrink-0" onClick={reset}><X size={14} className="mr-1" />Effacer</Button>}
      </div>

      {phase === "loading" && <div className="space-y-2"><Skeleton className="h-12 w-full" /><Skeleton className="h-12 w-full" /></div>}
      {phase === "error" && (
        <Card className="p-6 text-center space-y-3" data-testid="articles-error">
          <AlertTriangle className="mx-auto text-amber-500" size={20} />
          <p className="text-sm text-slate-700">{error}</p>
          <Button variant="outline" onClick={load}><RefreshCw size={14} className="mr-1.5" />Réessayer</Button>
        </Card>
      )}
      {phase === "ready" && data.items.length === 0 && (
        <Card className="p-8 text-center space-y-2" data-testid="articles-empty">
          <BookOpen className="mx-auto text-slate-300" size={26} />
          {filtered ? <><h3 className="font-medium text-slate-900">Aucun article ne correspond</h3><Button variant="outline" size="sm" onClick={reset}>Effacer la recherche</Button></> : (
            <><h3 className="font-medium text-slate-900">{EMPTY[view][0]}</h3><p className="text-sm text-slate-500 max-w-md mx-auto">{canManage || view === "published" ? EMPTY[view][1] : ""}</p>
              {canManage && view !== "archived" && view !== "to_review" && <Button asChild size="sm"><Link to="/support/connaissances/nouveau"><Plus size={14} className="mr-1.5" />Nouvel article</Link></Button>}</>
          )}
        </Card>
      )}
      {phase === "ready" && data.items.length > 0 && (
        <>
          <Card className="hidden md:block overflow-hidden" data-testid="articles-table">
            <Table>
              <TableHeader><TableRow><TableHead>Article</TableHead><TableHead>Catégorie</TableHead><TableHead>Propriétaire</TableHead><TableHead className="hidden lg:table-cell">Dernière relecture</TableHead></TableRow></TableHeader>
              <TableBody>
                {data.items.map((a) => (
                  <TableRow key={a.id} className="cursor-pointer" onClick={() => open(a)} data-testid={`article-row-${a.number}`}>
                    <TableCell className="max-w-lg"><div className="font-medium text-slate-900 truncate">{a.title}</div><div className="mt-0.5 flex items-center gap-2"><span className="text-xs text-slate-500 tabular-nums">{a.number}</span><Chips a={a} testid={`article-state-${a.number}`} /></div></TableCell>
                    <TableCell className="text-sm">{a.category_label}</TableCell>
                    <TableCell className="text-sm">{a.owner.name || "—"}</TableCell>
                    <TableCell className="hidden lg:table-cell text-sm text-slate-600">{a.last_review_on ? formatDay(a.last_review_on) : <span className="text-slate-400">—</span>}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
          <Card className="md:hidden divide-y divide-slate-100" data-testid="articles-cards">
            {data.items.map((a) => (
              <button key={a.id} type="button" onClick={() => open(a)} className="flex w-full items-start gap-3 px-4 py-3 text-left" data-testid={`article-card-${a.number}`}>
                <div className="min-w-0 flex-1 space-y-1">
                  <div className="font-medium text-slate-900">{a.title}</div>
                  <div className="text-xs text-slate-500">{a.number} · {a.category_label}</div>
                  <Chips a={a} testid={`article-card-state-${a.number}`} />
                  <div className="text-xs text-slate-600">Propriétaire : {a.owner.name || "—"}</div>
                </div>
                <ChevronRight size={16} className="text-slate-300 shrink-0 mt-1" />
              </button>
            ))}
          </Card>
          <p className="text-xs text-slate-500" data-testid="articles-total">{data.total} article{data.total > 1 ? "s" : ""}</p>
        </>
      )}
    </div>
  );
}
